import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Bold,
  Italic,
  Code,
  Link,
  List,
  ListOrdered,
  Heading1,
  Heading2,
  Heading3,
  Quote,
  Eye,
  Edit,
  Columns,
  Save,
} from 'lucide-react';
import { Button } from '../ui/Button';
import { MarkdownRenderer } from './MarkdownRenderer';

interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  minHeight?: number;
  autoSaveKey?: string;
  autoSaveInterval?: number;
  label?: string;
  description?: string;
  isDarkMode?: boolean;
}

type ViewMode = 'edit' | 'preview' | 'split';

interface ToolbarButton {
  icon: React.ReactNode;
  label: string;
  action: () => void;
  shortcut?: string;
}

export function MarkdownEditor({
  value,
  onChange,
  placeholder = 'Digite o conteúdo em markdown...',
  minHeight = 300,
  autoSaveKey,
  autoSaveInterval = 30000,
  label,
  description,
  isDarkMode = false,
}: MarkdownEditorProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('split');
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Theme-aware colors
  const colors = isDarkMode ? {
    containerBorder: 'border-gray-700',
    headerBg: 'bg-gray-800',
    headerBorder: 'border-gray-700',
    labelText: 'text-gray-200',
    descriptionText: 'text-gray-400',
    toolbarBg: 'bg-gray-800',
    toolbarBorder: 'border-gray-700',
    toolbarButton: 'text-gray-400 hover:text-white hover:bg-gray-700',
    viewModeBg: 'bg-gray-700',
    viewModeActive: 'bg-gray-600 text-white shadow-sm',
    viewModeInactive: 'text-gray-400 hover:text-white',
    savedText: 'text-gray-400',
    textareaBg: 'bg-gray-900',
    textareaText: 'text-gray-100',
    previewBg: 'bg-gray-800',
    previewText: 'text-gray-400',
    footerBg: 'bg-gray-800',
    footerBorder: 'border-gray-700',
    footerText: 'text-gray-400',
    divider: 'divide-gray-700',
  } : {
    containerBorder: 'border-gray-200',
    headerBg: 'bg-gray-50',
    headerBorder: 'border-gray-200',
    labelText: 'text-gray-700',
    descriptionText: 'text-gray-500',
    toolbarBg: 'bg-gray-50',
    toolbarBorder: 'border-gray-200',
    toolbarButton: 'text-gray-600 hover:text-gray-900 hover:bg-gray-200',
    viewModeBg: 'bg-gray-200',
    viewModeActive: 'bg-white text-gray-900 shadow-sm',
    viewModeInactive: 'text-gray-600 hover:text-gray-900',
    savedText: 'text-gray-500',
    textareaBg: 'bg-white',
    textareaText: 'text-gray-900',
    previewBg: 'bg-white',
    previewText: 'text-gray-400',
    footerBg: 'bg-gray-50',
    footerBorder: 'border-gray-200',
    footerText: 'text-gray-500',
    divider: 'divide-gray-200',
  };

  // Auto-save to localStorage
  useEffect(() => {
    if (!autoSaveKey) return;

    const interval = setInterval(() => {
      if (value) {
        localStorage.setItem(`markdown-draft-${autoSaveKey}`, value);
        setLastSaved(new Date());
      }
    }, autoSaveInterval);

    return () => clearInterval(interval);
  }, [value, autoSaveKey, autoSaveInterval]);

  // Load draft from localStorage on mount
  useEffect(() => {
    if (!autoSaveKey) return;

    const draft = localStorage.getItem(`markdown-draft-${autoSaveKey}`);
    if (draft && !value) {
      onChange(draft);
    }
  }, [autoSaveKey]);

  // Insert text at cursor position
  const insertText = useCallback((before: string, after: string = '', placeholder: string = '') => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end) || placeholder;
    
    const newValue = 
      value.substring(0, start) + 
      before + 
      selectedText + 
      after + 
      value.substring(end);
    
    onChange(newValue);

    // Restore cursor position
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + before.length + selectedText.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  }, [value, onChange]);

  // Insert at line start
  const insertAtLineStart = useCallback((prefix: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const lineStart = value.lastIndexOf('\n', start - 1) + 1;
    
    const newValue = 
      value.substring(0, lineStart) + 
      prefix + 
      value.substring(lineStart);
    
    onChange(newValue);

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + prefix.length, start + prefix.length);
    }, 0);
  }, [value, onChange]);

  const toolbarButtons: ToolbarButton[] = [
    {
      icon: <Heading1 className="w-4 h-4" />,
      label: 'Título 1',
      action: () => insertAtLineStart('# '),
      shortcut: 'Ctrl+1',
    },
    {
      icon: <Heading2 className="w-4 h-4" />,
      label: 'Título 2',
      action: () => insertAtLineStart('## '),
      shortcut: 'Ctrl+2',
    },
    {
      icon: <Heading3 className="w-4 h-4" />,
      label: 'Título 3',
      action: () => insertAtLineStart('### '),
      shortcut: 'Ctrl+3',
    },
    {
      icon: <Bold className="w-4 h-4" />,
      label: 'Negrito',
      action: () => insertText('**', '**', 'texto'),
      shortcut: 'Ctrl+B',
    },
    {
      icon: <Italic className="w-4 h-4" />,
      label: 'Itálico',
      action: () => insertText('*', '*', 'texto'),
      shortcut: 'Ctrl+I',
    },
    {
      icon: <Code className="w-4 h-4" />,
      label: 'Código',
      action: () => insertText('`', '`', 'código'),
      shortcut: 'Ctrl+`',
    },
    {
      icon: <Link className="w-4 h-4" />,
      label: 'Link',
      action: () => insertText('[', '](url)', 'texto'),
      shortcut: 'Ctrl+K',
    },
    {
      icon: <List className="w-4 h-4" />,
      label: 'Lista',
      action: () => insertAtLineStart('- '),
    },
    {
      icon: <ListOrdered className="w-4 h-4" />,
      label: 'Lista Numerada',
      action: () => insertAtLineStart('1. '),
    },
    {
      icon: <Quote className="w-4 h-4" />,
      label: 'Citação',
      action: () => insertAtLineStart('> '),
    },
  ];

  // Handle keyboard shortcuts
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.ctrlKey || e.metaKey) {
      switch (e.key) {
        case 'b':
          e.preventDefault();
          insertText('**', '**', 'texto');
          break;
        case 'i':
          e.preventDefault();
          insertText('*', '*', 'texto');
          break;
        case 'k':
          e.preventDefault();
          insertText('[', '](url)', 'texto');
          break;
        case '1':
          e.preventDefault();
          insertAtLineStart('# ');
          break;
        case '2':
          e.preventDefault();
          insertAtLineStart('## ');
          break;
        case '3':
          e.preventDefault();
          insertAtLineStart('### ');
          break;
      }
    }
  }, [insertText, insertAtLineStart]);

  const clearDraft = useCallback(() => {
    if (autoSaveKey) {
      localStorage.removeItem(`markdown-draft-${autoSaveKey}`);
      setLastSaved(null);
    }
  }, [autoSaveKey]);

  return (
    <div className={`border ${colors.containerBorder} rounded-lg overflow-hidden`}>
      {/* Header */}
      {(label || description) && (
        <div className={`px-4 py-2 ${colors.headerBg} border-b ${colors.headerBorder}`}>
          {label && <label className={`block text-sm font-medium ${colors.labelText}`}>{label}</label>}
          {description && <p className={`text-xs ${colors.descriptionText} mt-0.5`}>{description}</p>}
        </div>
      )}

      {/* Toolbar */}
      <div className={`flex items-center justify-between px-2 py-1 ${colors.toolbarBg} border-b ${colors.toolbarBorder}`}>
        <div className="flex items-center gap-0.5">
          {toolbarButtons.map((btn, idx) => (
            <button
              key={idx}
              type="button"
              onClick={btn.action}
              title={`${btn.label}${btn.shortcut ? ` (${btn.shortcut})` : ''}`}
              className={`p-1.5 ${colors.toolbarButton} rounded transition-colors`}
            >
              {btn.icon}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-1">
          {lastSaved && (
            <span className={`text-xs ${colors.savedText} mr-2`}>
              <Save className="w-3 h-3 inline mr-1" />
              Salvo às {lastSaved.toLocaleTimeString()}
            </span>
          )}
          
          <div className={`flex items-center ${colors.viewModeBg} rounded p-0.5`}>
            <button
              type="button"
              onClick={() => setViewMode('edit')}
              className={`p-1 rounded transition-colors ${
                viewMode === 'edit' ? colors.viewModeActive : colors.viewModeInactive
              }`}
              title="Modo Edição"
            >
              <Edit className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => setViewMode('split')}
              className={`p-1 rounded transition-colors ${
                viewMode === 'split' ? colors.viewModeActive : colors.viewModeInactive
              }`}
              title="Modo Dividido"
            >
              <Columns className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => setViewMode('preview')}
              className={`p-1 rounded transition-colors ${
                viewMode === 'preview' ? colors.viewModeActive : colors.viewModeInactive
              }`}
              title="Modo Preview"
            >
              <Eye className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Editor Area */}
      <div 
        className={`flex ${viewMode === 'split' ? `divide-x ${colors.divider}` : ''}`}
        style={{ minHeight }}
      >
        {/* Editor */}
        {(viewMode === 'edit' || viewMode === 'split') && (
          <div className={viewMode === 'split' ? 'w-1/2' : 'w-full'}>
            <textarea
              ref={textareaRef}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              className={`w-full h-full p-4 font-mono text-sm resize-none focus:outline-none ${colors.textareaBg} ${colors.textareaText}`}
              style={{ minHeight }}
            />
          </div>
        )}

        {/* Preview */}
        {(viewMode === 'preview' || viewMode === 'split') && (
          <div 
            className={`${viewMode === 'split' ? 'w-1/2' : 'w-full'} p-4 overflow-auto ${colors.previewBg}`}
            style={{ minHeight }}
          >
            {value ? (
              <MarkdownRenderer content={value} darkMode={isDarkMode} />
            ) : (
              <p className={`${colors.previewText} italic`}>Preview aparecerá aqui...</p>
            )}
          </div>
        )}
      </div>

      {/* Footer with draft info */}
      {autoSaveKey && (
        <div className={`px-4 py-2 ${colors.footerBg} border-t ${colors.footerBorder} flex items-center justify-between`}>
          <span className={`text-xs ${colors.footerText}`}>
            Auto-save ativado (a cada {autoSaveInterval / 1000}s)
          </span>
          {lastSaved && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearDraft}
              className="text-xs"
            >
              Limpar rascunho
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

export default MarkdownEditor;
